import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';

interface UseScreenShareOptions {
  onStreamStart?: (stream: MediaStream) => void;
  onStreamEnd?: () => void;
}

interface ScreenShareState {
  isSharing: boolean;
  stream: MediaStream | null;
  error: string | null;
}

export const useScreenShare = (options?: UseScreenShareOptions) => {
  const [state, setState] = useState<ScreenShareState>({
    isSharing: false,
    stream: null,
    error: null,
  });

  const streamRef = useRef<MediaStream | null>(null);

  const startScreenShare = useCallback(async () => {
    try {
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen sharing is not supported in this browser');
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Listen for when user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };

      setState({
        isSharing: true,
        stream,
        error: null,
      });

      options?.onStreamStart?.(stream);
      toast.success('Screen sharing started');

      return stream;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start screen sharing';
      
      // User cancelled - don't show error
      if (errorMessage.includes('Permission denied') || errorMessage.includes('NotAllowedError')) {
        setState(prev => ({ ...prev, error: null }));
        return null;
      }

      console.error('Screen share error:', errorMessage);
      setState(prev => ({ ...prev, error: errorMessage }));
      toast.error('Failed to start screen sharing');
      return null;
    }
  }, [options]);

  const stopScreenShare = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }

    setState({
      isSharing: false,
      stream: null,
      error: null,
    });

    options?.onStreamEnd?.();
    toast.info('Screen sharing stopped');
  }, [options]);

  const toggleScreenShare = useCallback(async () => {
    if (state.isSharing) {
      stopScreenShare();
      return null;
    } else {
      return await startScreenShare();
    }
  }, [state.isSharing, startScreenShare, stopScreenShare]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return {
    ...state,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
  };
};

export default useScreenShare;
